import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import api from "../services/api";
import { toast } from "react-toastify";
import EmployeeLayout from "../components/EmployeeLayout";
import bookingWS from "../services/bookingWebSocket";
import locationWS from "../services/locationWebSocket";
import { getMainForkBookingDetails } from "../services/bookingService";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { fetchBookings } from "../actions/BookingActions";
import { fetchUsers } from "../actions/UserActions";
import { fetchVehicles } from "../actions/VehicleActions";
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Phone, 
  Navigation, 
  X, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  Trash2,
  Eye,
  Edit2
} from "lucide-react";

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const startIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const endIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/149/149060.png",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// ORS Geocoding helper
const fetchORSResults = async (query) => {
  const apiKey = process.env.REACT_APP_ORS_KEY;
  if (!apiKey || !query.trim()) return [];
  try {
    const url = `https://api.openrouteservice.org/geocode/search?api_key=${apiKey}&text=${encodeURIComponent(query)}&boundary.country=LK&size=5`;
    const res = await fetch(url);
    const data = await res.json();
    return data.features || [];
  } catch (err) {
    console.error("ORS search error:", err);
    return [];
  }
};

// Fetch route from OpenRouteService
const fetchRoute = async (start, end) => {
  const apiKey = process.env.REACT_APP_ORS_KEY;
  if (!apiKey) return null;

  const url = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${start[1]},${start[0]}&end=${end[1]},${end[0]}`;

  try {
    const res = await fetch(url);
    const data = await res.json();
    const feature = data.features?.[0];
    if (feature?.geometry?.coordinates && feature?.properties?.segments?.[0]) {
      const segment = feature.properties.segments[0];
      const geometry = feature.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
      const distance = segment.distance;
      const duration = segment.duration;
      return { geometry, distance, duration };
    }
  } catch (e) {
    console.error("ORS route error:", e);
  }
  return null;
};

// Route Layer Component
function ModalRouteLayer({ start, end, onRouteUpdate }) {
  const [route, setRoute] = useState([]);
  const map = useMap();

  useEffect(() => {
    if (!start || !end) {
      setRoute([]);
      onRouteUpdate(null);
      return;
    }

    (async () => {
      const result = await fetchRoute(start, end);
      if (result) {
        setRoute(result.geometry);
        onRouteUpdate({
          geometry: result.geometry,
          distance: result.distance,
          duration: result.duration,
        });
      }
    })();
  }, [start, end, map, onRouteUpdate]);

  return route.length > 0 ? (
    <Polyline positions={route} color="#3B82F6" weight={3} opacity={0.7} />
  ) : null;
}

// Location Picker for Modal
function ModalLocationPicker({ updateModal, setUpdateModal, isInProgress }) {
  const [clickCount, setClickCount] = React.useState(0);

  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      const latF = lat.toFixed(6);
      const lngF = lng.toFixed(6);
      const coordinateText = `${latF}, ${lngF}`;

      if (isInProgress) {
        setUpdateModal((prev) => ({
          ...prev,
          newEndLat: latF,
          newEndLng: lngF,
          endSearch: coordinateText,
        }));
      } else {
        if (clickCount % 2 === 0) {
          setUpdateModal((prev) => ({
            ...prev,
            newStartLat: latF,
            newStartLng: lngF,
            startSearch: coordinateText,
            mapCenter: [parseFloat(latF), parseFloat(lngF)],
          }));
        } else {
          setUpdateModal((prev) => ({
            ...prev,
            newEndLat: latF,
            newEndLng: lngF,
            endSearch: coordinateText,
          }));
        }
        setClickCount((prev) => prev + 1);
      }
    },
  });

  return null;
}

// Booking Card Component
function BookingCard({ booking, drivers, onTrack, onCall, onCancel, onUpdate, cancellingBookings, isPending, isActive, isEnRoute, isInProgress, isCompleted, isCancelled, getStatusInfo }) {
  const driverMobileNum = booking.driverMobile || drivers[booking.dSNo]?.mobile;
  const hasDriver = booking.dSNo && driverMobileNum;
  const statusInfo = getStatusInfo(booking.status);
  const isCancelling = cancellingBookings.has(booking.bId);

  return (
    <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition">
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-100 p-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Booking #{booking.bId}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {new Date(booking.bookingDT).toLocaleDateString()} • {new Date(booking.bookingDT).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
            {statusInfo.label === "Pending" && <Clock size={10} />}
            {statusInfo.label === "Active" && <CheckCircle size={10} />}
            {statusInfo.label === "En Route" && <Navigation size={10} />}
            {statusInfo.label === "In Progress" && <RefreshCw size={10} />}
            {statusInfo.label === "Completed" && <CheckCircle size={10} />}
            {statusInfo.label === "Cancelled" && <XCircle size={10} />}
            {statusInfo.label}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 bg-emerald-500 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold mt-0.5">
            A
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-500">Pickup Location</p>
            <p className="text-xs text-gray-900 font-mono">
              {parseFloat(booking.slatitude).toFixed(4)}, {parseFloat(booking.slongitude).toFixed(4)}
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 bg-red-500 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold mt-0.5">
            B
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-500">Drop Location</p>
            <p className="text-xs text-gray-900 font-mono">
              {parseFloat(booking.elatitude).toFixed(4)}, {parseFloat(booking.elongitude).toFixed(4)}
            </p>
          </div>
        </div>

        {booking.vId && (
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-900">{booking.vId}</p>
            <p className="text-xs text-gray-500">{booking.driverName || booking.dSNo || "Assigned"}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-3 bg-gray-50 border-t border-gray-100 space-y-2">
        {isPending(booking.status) && (
          <button
            onClick={() => onCancel(booking.bId)}
            disabled={isCancelling}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50"
          >
            {isCancelling ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Cancel Booking
          </button>
        )}

        {isActive(booking.status) && (
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => onTrack(booking)}
              className="inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition"
            >
              <Eye size={12} />
              Track
            </button>
            <button
              onClick={() => onCall(booking)}
              disabled={!hasDriver}
              className={`inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition ${
                hasDriver ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              <Phone size={12} />
              Call
            </button>
            <button
              onClick={() => onCancel(booking.bId)}
              disabled={isCancelling}
              className="inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50"
            >
              {isCancelling ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Cancel
            </button>
          </div>
        )}

        {isEnRoute(booking.status) && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
              <Navigation size={12} className="text-blue-600 animate-pulse" />
              <span className="text-xs font-medium text-blue-700">Driver on the way!</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => onTrack(booking)} className="inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition">
                <Eye size={12} /> Track
              </button>
              <button onClick={() => onCall(booking)} disabled={!hasDriver} className={`inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition ${hasDriver ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}>
                <Phone size={12} /> Call
              </button>
              <button onClick={() => onCancel(booking.bId)} disabled={isCancelling} className="inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50">
                {isCancelling ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />} Cancel
              </button>
            </div>
          </div>
        )}

        {isInProgress(booking.status) && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
              <RefreshCw size={12} className="text-purple-600 animate-spin" />
              <span className="text-xs font-medium text-purple-700">Trip in progress</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => onTrack(booking)} className="inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition">
                <Eye size={12} /> Track
              </button>
              <button onClick={() => onCall(booking)} disabled={!hasDriver} className={`inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition ${hasDriver ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}>
                <Phone size={12} /> Call
              </button>
            </div>
          </div>
        )}

        {isCompleted(booking.status) && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
              <CheckCircle size={12} className="text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">Completed</span>
            </div>
            <button onClick={() => onCall(booking)} disabled={!hasDriver} className={`w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition ${hasDriver ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}>
              <Phone size={12} /> Call Driver
            </button>
          </div>
        )}

        {isCancelled(booking.status) && (
          <div className="text-center py-3 bg-red-50 rounded-lg">
            <XCircle size={16} className="mx-auto text-red-600 mb-1" />
            <p className="text-xs text-red-600 font-medium">This booking has been cancelled</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Stats Card Component
function StatCard({ title, value, icon: Icon, color, onClick }) {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-600",
    green: "bg-emerald-50 border-emerald-200 text-emerald-600",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-600",
    purple: "bg-purple-50 border-purple-200 text-purple-600",
    red: "bg-red-50 border-red-200 text-red-600",
  };
  
  const bgColor = colorClasses[color] || colorClasses.blue;
  
  return (
    <div
      onClick={onClick}
      className={`${bgColor} border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md`}
    >
      <div className="flex items-center justify-between mb-1">
        <Icon size={14} className={bgColor.split(' ')[2]} />
        <span className="text-lg font-bold">{value}</span>
      </div>
      <p className="text-xs font-medium">{title}</p>
    </div>
  );
}

export default function EmployeeMyBookings() {
  const dispatch = useDispatch();
  const { authKey, userDetails } = useSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [scheduledBookings, setScheduledBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [drivers, setDrivers] = useState({});
  const [activeFilter, setActiveFilter] = useState(searchParams.get("filter") || "all");
  const [bookingType, setBookingType] = useState("realtime");
  const [cancellingBookings, setCancellingBookings] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [updateModal, setUpdateModal] = useState({
    isOpen: false,
    booking: null,
    newStartLat: "",
    newStartLng: "",
    newEndLat: "",
    newEndLng: "",
    distance: null,
    mapCenter: [6.9271, 79.8612],
    startSearch: "",
    endSearch: "",
    startSuggestions: [],
    endSuggestions: [],
    routeInfo: null,
  });

  const getBookings = async () => dispatch(fetchBookings());
  const getUsers = async () => dispatch(fetchUsers());
  const getVehicles = async () => dispatch(fetchVehicles());

  const isScheduledBookingStatus = (value) => {
    const status = String(value || "").trim().toUpperCase();
    return status === "S" || status === "SHEDULE BOOKING" || status === "SCHEDULE BOOKING";
  };

  const isRealtimeBookingStatus = (value) => {
    const status = String(value || "").trim().toUpperCase();
    return status === "R" || status === "REAL-TIME BOOKING" || status === "REAL TIME BOOKING";
  };

  useEffect(() => {
    const loadMainBookingsByType = async () => {
      if (!authKey || !userDetails?.ServiceNo) return;

      if (bookingType === "scheduled") {
        setLoadingScheduled(true);
      } else {
        setLoading(true);
      }

      try {
        const data = await getMainForkBookingDetails();

        if (Array.isArray(data)) {
          const myData = data.filter(
            (b) => String(b.bookingSno || "").trim() === String(userDetails.ServiceNo).trim()
          );

          const scheduled = myData.filter((b) => isScheduledBookingStatus(b.BookingStatus));
          const realtime = myData.filter((b) => isRealtimeBookingStatus(b.BookingStatus));

          const sortByIdDesc = (a, b) => parseInt(b.bId || 0, 10) - parseInt(a.bId || 0, 10);

          setBookings(realtime.sort(sortByIdDesc));
          setScheduledBookings(scheduled.sort(sortByIdDesc));
        } else {
          setBookings([]);
          setScheduledBookings([]);
        }

        setCurrentPage(1);
      } catch (err) {
        console.error("Failed to load main bookings:", err);
        toast.error("Failed to load bookings");
      } finally {
        setLoading(false);
        setLoadingScheduled(false);
      }
    };

    loadMainBookingsByType();
  }, [authKey, userDetails?.ServiceNo, bookingType]);

  useEffect(() => {
    const filterParam = searchParams.get("filter");
    setActiveFilter(filterParam || "all");
    setCurrentPage(1);
  }, [searchParams]);

  const sourceBookings = bookingType === "scheduled" ? scheduledBookings : bookings;

  const filteredBookings = sourceBookings.filter((booking) => {
    if (activeFilter === "all") return true;
    const status = (booking.status || "").toString().trim().toUpperCase();
    if (activeFilter === "pending") return status === "P" || status === "PENDING";
    if (activeFilter === "active") return status === "A" || status === "ACTIVE";
    if (activeFilter === "completed") return status === "CO" || status === "COMPLETED";
    if (activeFilter === "cancelled") return status === "C" || status === "CANCELLED" || status === "CANCELED";
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBookings = filteredBookings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  // WebSocket Connection
  useEffect(() => {
    if (!authKey || !userDetails?.ServiceNo) return;

    if (!bookingWS.isConnected) {
      bookingWS.connectAsEmployee(authKey);
    }

    const handleStatusUpdate = (message) => {
      if (message.bookingSno && userDetails?.ServiceNo && message.bookingSno.toString() !== userDetails.ServiceNo.toString()) {
        return;
      }

      if (message.bId && message.status) {
        const normalizedStatus = normalizeStatus(message.status);

        const isAdminCancelled = normalizedStatus === "cancelled" && 
          (message.cancelledBy === "admin" || message.type === "booking_cancelled" || message.action === "booking_cancelled");

        if (isAdminCancelled) {
          toast.dismiss();
          toast.error(message.message || `Booking #${message.bId} cancelled by Admin`, { autoClose: 5000 });
        }

        setBookings((prev) => prev.map((booking) => 
          booking.bId?.toString() === message.bId?.toString() 
            ? { ...booking, status: normalizedStatus, dlatitude: message.dlatitude !== undefined ? message.dlatitude : booking.dlatitude, dlongitude: message.dlongitude !== undefined ? message.dlongitude : booking.dlongitude }
            : booking
        ));

        setScheduledBookings((prev) => prev.map((booking) => 
          booking.bId?.toString() === message.bId?.toString() 
            ? { ...booking, status: normalizedStatus, dlatitude: message.dlatitude !== undefined ? message.dlatitude : booking.dlatitude, dlongitude: message.dlongitude !== undefined ? message.dlongitude : booking.dlongitude }
            : booking
        ));

        if (!isAdminCancelled) {
          showStatusChangeNotification("", normalizedStatus);
        }
      }
    };

    const unsubscribe = bookingWS.onStatusUpdate(handleStatusUpdate);
    return () => { if (unsubscribe) unsubscribe(); };
  }, [authKey, userDetails]);

  const normalizeStatus = (status) => {
    if (!status) return "pending";
    const s = status.toString().trim().toLowerCase();
    if (s === "p" || s === "pending") return "pending";
    if (s === "a" || s === "active") return "active";
    if (s === "en" || s === "enroute") return "enroute";
    if (s === "ip" || s === "inprogress" || s === "in progress") return "inprogress";
    if (s === "co" || s === "completed") return "completed";
    if (s === "c" || s === "canceled" || s === "cancelled" || s === "cancel") return "cancelled";
    return s;
  };

  const showStatusChangeNotification = (oldStatus, newStatus) => {
    const messages = {
      pending: "🟡 Booking is waiting for driver acceptance",
      active: "✅ Driver has accepted your booking",
      enroute: "🚗 Driver is on the way to pickup",
      inprogress: "⏱️ Trip is in progress",
      completed: "🎉 Trip completed successfully",
      cancelled: "❌ Booking has been cancelled",
    };

    const message = messages[newStatus] || `Status updated to ${newStatus}`;
    if (normalizeStatus(oldStatus) !== newStatus) {
      toast.info(message, { autoClose: 5000 });
    }
  };

  // Fetch driver details
  useEffect(() => {
    const fetchDriverInfo = async () => {
      try {
        const userList = await getUsers();
        const driverMap = {};
        userList.forEach((user) => {
          driverMap[user.ServiceNo] = { name: user.Name, mobile: user.MobileNo };
        });
        setDrivers(driverMap);
      } catch (err) {
        console.error("Failed to fetch driver info:", err);
      }
    };
    fetchDriverInfo();
  }, [authKey]);

  const handleCancel = async (bId) => {
    if (cancellingBookings.has(bId)) return;
    if (!window.confirm("Cancel this booking?")) return;

    setCancellingBookings((prev) => new Set(prev).add(bId));

    try {
      const bookingToCancel = bookings.find((b) => b.bId === bId);
      if (!bookingToCancel) {
        toast.error("Booking not found");
        return;
      }

      const payload = [{ ...bookingToCancel, status: "C" }];
      await api.post("/BookingRegister/PostForkBooking", payload);

      setTimeout(() => {
        if (bookingWS.isReady()) {
          bookingWS.sendCancellation({
            bId: parseInt(bId),
            dSNo: bookingToCancel.dSNo,
            vId: bookingToCancel.vId,
            type: "booking_cancelled",
            action: "booking_cancelled",
            bookingSno: userDetails.ServiceNo,
          });
        }
      }, 100);

      if (bookingToCancel.vId) {
        try {
          const allVehicles = await getVehicles();
          const vehicle = allVehicles?.find((v) => v.vehicleId?.toString() === bookingToCancel.vId?.toString());
          if (vehicle) {
            const typeCodeMap = { Car: "C", Van: "V", Lorry: "L", SUV: "S", Cab: "CA", Bus: "B", "Cherry Picker": "CH", Forklift: "F", Crane: "CR", Trailer: "T" };
            const vehicleTypeCode = typeCodeMap[vehicle.vehicleType] || vehicle.vehicleType;
            const statusCode = vehicle.status === "Inactive" ? "I" : "A";
            const oStatusCode = vehicle.oStatus === "Online" ? "O" : "F";

            const vehiclePayload = [{
              vehicleId: vehicle.vehicleId,
              vehicleType: vehicleTypeCode,
              weightCat: vehicle.weightCat || "",
              driverSno: vehicle.driverSno || "",
              status: statusCode,
              bStatus: "",
              oStatus: oStatusCode,
              latitude: vehicle.latitude || "",
              longitude: vehicle.longitude || "",
            }];

            await api.post("/VehicleRegister/PostVehicleDetails", vehiclePayload);

            setTimeout(() => {
              locationWS.updateDriverData({
                vehicleId: bookingToCancel.vId,
                vehicle_type: vehicle.vehicleType,
                vehicleType: vehicle.vehicleType,
                latitude: vehicle.latitude || "",
                longitude: vehicle.longitude || "",
                bStatus: "",
                oStatus: oStatusCode,
                status: statusCode,
              });
              if (locationWS.isReady && locationWS.isReady()) {
                locationWS.sendLocationUpdate();
              }
            }, 200);
          }
        } catch (vehicleErr) {
          console.error("Failed to clear vehicle bStatus:", vehicleErr);
        }
      }

      setBookings((prev) => prev.map((b) => b.bId === bId ? { ...b, status: "C" } : b));
      toast.success("Booking cancelled successfully");
    } catch (err) {
      console.error("Cancel failed:", err);
      toast.error("Cancel failed");
    } finally {
      setCancellingBookings((prev) => {
        const newSet = new Set(prev);
        newSet.delete(bId);
        return newSet;
      });
    }
  };

  const handleTrack = async (b) => {
    try {
      const bookings = await getBookings();
      const booking = bookings.find((bk) => bk.bId?.toString() === b.bId?.toString());
      if (!booking) {
        toast.error("Booking not found");
        return;
      }

      const status = (booking.status || "").toString().trim().toUpperCase();
      if (["A", "ACTIVE", "EN", "ENROUTE", "IP", "INPROGRESS", "IN PROGRESS"].includes(status)) {
        window.open(`/track?bId=${b.bId}`, "_blank");
        return;
      }

      if (["CO", "COMPLETED"].includes(status)) {
        toast.info("This booking has already been completed");
      } else if (["C", "CANCEL", "CANCELLED", "CANCELED"].includes(status)) {
        toast.warn("This booking has been cancelled");
      } else if (["P", "PENDING"].includes(status)) {
        toast.info("Booking is still pending driver acceptance");
      }
      window.location.reload();
    } catch (error) {
      console.error("Error checking booking status:", error);
      toast.error("Failed to verify booking status");
    }
  };

  const handleCall = (booking) => {
    const driverMobile = booking.driverMobile || drivers[booking.dSNo]?.mobile;
    if (!driverMobile || driverMobile === "N/A") {
      toast.warn("Driver phone number not available");
      return;
    }
    window.location.href = `tel:${driverMobile}`;
  };

  const getStatusInfo = (status) => {
    const s = normalizeStatus(status);
    if (s === "pending") return { label: "Pending", color: "bg-yellow-50 text-yellow-700 border-yellow-200" };
    if (s === "active") return { label: "Active", color: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    if (s === "enroute") return { label: "En Route", color: "bg-blue-50 text-blue-700 border-blue-200" };
    if (s === "inprogress") return { label: "In Progress", color: "bg-purple-50 text-purple-700 border-purple-200" };
    if (s === "completed") return { label: "Completed", color: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    if (s === "cancelled") return { label: "Cancelled", color: "bg-red-50 text-red-700 border-red-200" };
    return { label: status || "Unknown", color: "bg-gray-50 text-gray-700 border-gray-200" };
  };

  const isPending = (status) => normalizeStatus(status) === "pending";
  const isActive = (status) => normalizeStatus(status) === "active";
  const isEnRoute = (status) => normalizeStatus(status) === "enroute";
  const isInProgress = (status) => normalizeStatus(status) === "inprogress";
  const isCompleted = (status) => normalizeStatus(status) === "completed";
  const isCancelled = (status) => normalizeStatus(status) === "cancelled";

  // Calculate distance
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const openUpdateModal = (booking) => {
    setUpdateModal({
      isOpen: true,
      booking,
      newStartLat: booking.slatitude,
      newStartLng: booking.slongitude,
      newEndLat: booking.elatitude,
      newEndLng: booking.elongitude,
      distance: parseFloat(booking.totalDestence) || null,
      mapCenter: [parseFloat(booking.slatitude), parseFloat(booking.slongitude)],
      startSearch: "",
      endSearch: "",
      startSuggestions: [],
      endSuggestions: [],
      routeInfo: null,
    });
  };

  const closeUpdateModal = () => {
    setUpdateModal({
      isOpen: false,
      booking: null,
      newStartLat: "",
      newStartLng: "",
      newEndLat: "",
      newEndLng: "",
      distance: null,
      mapCenter: [6.9271, 79.8612],
      startSearch: "",
      endSearch: "",
      startSuggestions: [],
      endSuggestions: [],
      routeInfo: null,
    });
  };

  const handleStartSearch = async (query) => {
    setUpdateModal((prev) => ({ ...prev, startSearch: query }));
    if (query.trim().length > 2) {
      const results = await fetchORSResults(query);
      setUpdateModal((prev) => ({ ...prev, startSuggestions: results }));
    } else {
      setUpdateModal((prev) => ({ ...prev, startSuggestions: [] }));
    }
  };

  const handleEndSearch = async (query) => {
    setUpdateModal((prev) => ({ ...prev, endSearch: query }));
    if (query.trim().length > 2) {
      const results = await fetchORSResults(query);
      setUpdateModal((prev) => ({ ...prev, endSuggestions: results }));
    } else {
      setUpdateModal((prev) => ({ ...prev, endSuggestions: [] }));
    }
  };

  const selectStartLocation = (feature) => {
    const [lng, lat] = feature.geometry.coordinates;
    const latF = lat.toFixed(6);
    const lngF = lng.toFixed(6);
    setUpdateModal((prev) => ({
      ...prev,
      newStartLat: latF,
      newStartLng: lngF,
      startSearch: feature.properties.label || "",
      startSuggestions: [],
      mapCenter: [lat, lng],
    }));
  };

  const selectEndLocation = (feature) => {
    const [lng, lat] = feature.geometry.coordinates;
    const latF = lat.toFixed(6);
    const lngF = lng.toFixed(6);
    setUpdateModal((prev) => ({
      ...prev,
      newEndLat: latF,
      newEndLng: lngF,
      endSearch: feature.properties.label || "",
      endSuggestions: [],
    }));
  };

  const clearMapLocations = () => {
    setUpdateModal((prev) => ({
      ...prev,
      newStartLat: "",
      newStartLng: "",
      newEndLat: "",
      newEndLng: "",
      startSearch: "",
      endSearch: "",
      startSuggestions: [],
      endSuggestions: [],
      routeInfo: null,
      mapCenter: updateModal.booking ? [parseFloat(updateModal.booking.slatitude), parseFloat(updateModal.booking.slongitude)] : [6.9271, 79.8612],
    }));
  };

  const handleModalLocationUpdate = async () => {
    if (!updateModal.booking) return;

    const { booking, newStartLat, newStartLng, newEndLat, newEndLng } = updateModal;
    const bookingStatus = booking.status?.toString().trim().toUpperCase();

    const canChangeStart = isPending(booking.status) || isActive(booking.status) || isEnRoute(booking.status);
    const canChangeEnd = isPending(booking.status) || isActive(booking.status) || isEnRoute(booking.status) || isInProgress(booking.status);

    if (!canChangeEnd || (!canChangeStart && canChangeEnd)) {
      toast.error("Cannot update locations for this booking status");
      return;
    }

    const startChanged = newStartLat !== booking.slatitude || newStartLng !== booking.slongitude;
    const endChanged = newEndLat !== booking.elatitude || newEndLng !== booking.elongitude;

    if (!startChanged && !endChanged) {
      toast.info("No changes made");
      return;
    }

    if (startChanged && !canChangeStart) {
      toast.error("Cannot change pickup location when trip is in progress");
      return;
    }

    if (!newStartLat || !newStartLng || !newEndLat || !newEndLng) {
      toast.error("Please select both locations");
      return;
    }

    try {
      const latestBookings = await getBookings();
      let latestBooking = booking;
      const b = latestBookings.find((item) => item.bId?.toString() === booking.bId?.toString());
      if (b) latestBooking = b;

      const apiKey = process.env.REACT_APP_ORS_KEY;
      let newDistance = calculateDistance(newStartLat, newStartLng, newEndLat, newEndLng);

      if (apiKey) {
        try {
          const routeUrl = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${newStartLng},${newStartLat}&end=${newEndLng},${newEndLat}`;
          const routeRes = await fetch(routeUrl);
          const routeData = await routeRes.json();
          const routeDistance = routeData.features?.[0]?.properties?.segments?.[0]?.distance;
          if (routeDistance) {
            newDistance = routeDistance / 1000;
          }
        } catch (err) {
          console.error("ORS route distance error:", err);
        }
      }

      const getStatusCode = (status) => {
        const s = status?.toString().trim().toUpperCase() || "";
        if (s === "ACTIVE" || s === "A") return "A";
        if (s === "ENROUTE" || s === "EN") return "EN";
        if (s === "INPROGRESS" || s === "IP") return "IP";
        if (s === "COMPLETED" || s === "CO") return "CO";
        if (s === "PENDING" || s === "P") return "P";
        return s;
      };

      const updatePayload = [{
        bId: latestBooking.bId,
        slatitude: canChangeStart ? newStartLat : latestBooking.slatitude,
        slongitude: canChangeStart ? newStartLng : latestBooking.slongitude,
        elatitude: newEndLat,
        elongitude: newEndLng,
        totalDestence: newDistance.toFixed(2),
        status: getStatusCode(latestBooking.status),
        bookingSno: latestBooking.bookingSno,
        vId: latestBooking.vId,
        dSNo: latestBooking.dSNo,
        bookingDT: latestBooking.bookingDT,
        bsTime: latestBooking.bsTime,
        beTime: latestBooking.beTime,
        dlatitude: latestBooking.dlatitude,
        dlongitude: latestBooking.dlongitude,
      }];

      const updateRes = await api.post("/BookingRegister/PostForkBooking", updatePayload);

      if (updateRes.data?.statusCode === 200) {
        let alertMessage = "";
        if (startChanged && canChangeStart) alertMessage += "✅ Pickup location changed. ";
        if (endChanged) alertMessage += "✅ End location changed. ";
        toast.success(alertMessage);
        closeUpdateModal();
        setTimeout(() => { window.location.reload(); }, 1500);
      } else {
        toast.error("Update failed: " + (updateRes.data?.result || "Unknown error"));
      }
    } catch (err) {
      console.error("Update failed:", err);
      toast.error("Failed to update locations");
    }
  };

  // Calculate stats
  const stats = {
    total: sourceBookings.length,
    pending: sourceBookings.filter(b => isPending(b.status)).length,
    active: sourceBookings.filter(b => isActive(b.status)).length,
    completed: sourceBookings.filter(b => isCompleted(b.status)).length,
    cancelled: sourceBookings.filter(b => isCancelled(b.status)).length,
  };

  if (loading && bookingType === "realtime") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-8 text-center">
          <RefreshCw size={24} className="mx-auto mb-3 text-gray-400 animate-spin" />
          <p className="text-sm text-gray-500">Loading real-time bookings...</p>
        </div>
      </div>
    );
  }

  if (loadingScheduled && bookingType === "scheduled") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-8 text-center">
          <RefreshCw size={24} className="mx-auto mb-3 text-gray-400 animate-spin" />
          <p className="text-sm text-gray-500">Loading scheduled bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <EmployeeLayout>
      {/* Header Section */}
      <div className="mb-4">
        <h1 className="text-lg sm:text-xl font-semibold text-gray-900">My Bookings</h1>
        <p className="text-xs text-gray-500 mt-0.5">Track and manage your vehicle bookings</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        <StatCard title="Total" value={stats.total} icon={Calendar} color="blue" onClick={() => { setActiveFilter("all"); setSearchParams({}); }} />
        <StatCard title="Pending" value={stats.pending} icon={Clock} color="yellow" onClick={() => { setActiveFilter("pending"); setSearchParams({ filter: "pending" }); }} />
        <StatCard title="Active" value={stats.active} icon={CheckCircle} color="green" onClick={() => { setActiveFilter("active"); setSearchParams({ filter: "active" }); }} />
        <StatCard title="Completed" value={stats.completed} icon={CheckCircle} color="purple" onClick={() => { setActiveFilter("completed"); setSearchParams({ filter: "completed" }); }} />
        <StatCard title="Cancelled" value={stats.cancelled} icon={XCircle} color="red" onClick={() => { setActiveFilter("cancelled"); setSearchParams({ filter: "cancelled" }); }} />
      </div>

      {/* Booking Type Buttons */}
      <div className="bg-white border border-gray-100 rounded-lg p-2 mb-4">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => { setBookingType("realtime"); setCurrentPage(1); setActiveFilter("all"); }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              bookingType === "realtime" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Real-Time Bookings
          </button>
          <button
            onClick={() => { setBookingType("scheduled"); setCurrentPage(1); setActiveFilter("all"); }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              bookingType === "scheduled" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Scheduled Bookings
          </button>
        </div>
      </div>

      {/* Filter Indicator */}
      {activeFilter !== "all" && (
        <div className="bg-white border border-gray-100 rounded-lg p-3 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm">🔍</span>
            <div>
              <p className="text-xs text-gray-500">Showing:</p>
              <p className="text-sm font-semibold text-gray-900 capitalize">
                {activeFilter} Bookings ({filteredBookings.length})
              </p>
            </div>
          </div>
          <button
            onClick={() => { setSearchParams({}); setActiveFilter("all"); }}
            className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition"
          >
            Clear Filter
          </button>
        </div>
      )}

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-12 text-center">
          <div className="flex flex-col items-center gap-3">
            <Calendar size={32} className="text-gray-300" />
            <div>
              <p className="text-sm font-medium text-gray-900 mb-1">
                {activeFilter !== "all" ? `No ${activeFilter} bookings found` : "You have no active bookings"}
              </p>
              {activeFilter !== "all" && (
                <button
                  onClick={() => { setSearchParams({}); setActiveFilter("all"); }}
                  className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                >
                  View All Bookings
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {currentBookings.map((booking) => (
              <BookingCard
                key={booking.bId}
                booking={booking}
                drivers={drivers}
                onTrack={handleTrack}
                onCall={handleCall}
                onCancel={handleCancel}
                onUpdate={openUpdateModal}
                cancellingBookings={cancellingBookings}
                isPending={isPending}
                isActive={isActive}
                isEnRoute={isEnRoute}
                isInProgress={isInProgress}
                isCompleted={isCompleted}
                isCancelled={isCancelled}
                getStatusInfo={getStatusInfo}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-gray-500">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredBookings.length)} of {filteredBookings.length}
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="px-2 py-1 text-xs text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Update Location Modal */}
      {updateModal.isOpen && updateModal.booking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-50 rounded-lg">
                  <Edit2 size={16} className="text-blue-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">Update Booking Locations</h2>
              </div>
              <button onClick={closeUpdateModal} className="p-1 hover:bg-gray-100 rounded-lg transition">
                <X size={16} className="text-gray-400" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Booking Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Booking ID: <span className="font-semibold text-gray-900">#{updateModal.booking.bId}</span></p>
                <p className="text-xs text-gray-500 mt-1">Status: <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs ${getStatusInfo(updateModal.booking.status).color}`}>{getStatusInfo(updateModal.booking.status).label}</span></p>
              </div>

              {/* Start Location */}
              {!isInProgress(updateModal.booking.status) && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">📍 Pickup Location</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search pickup location..."
                      value={updateModal.startSearch}
                      onChange={(e) => handleStartSearch(e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
                    />
                    {updateModal.startSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg mt-1 max-h-48 overflow-y-auto shadow-lg z-50">
                        {updateModal.startSuggestions.map((feature, idx) => (
                          <button key={idx} onClick={() => selectStartLocation(feature)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 border-b last:border-b-0">
                            {feature.properties.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {updateModal.newStartLat && updateModal.newStartLng && (
                    <p className="text-xs text-gray-500 font-mono">{updateModal.newStartLat}, {updateModal.newStartLng}</p>
                  )}
                </div>
              )}

              {isInProgress(updateModal.booking.status) && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">📍 Pickup Location (Fixed)</label>
                  <div className="bg-gray-100 rounded-lg p-2">
                    <p className="text-xs font-mono text-gray-600">{parseFloat(updateModal.booking.slatitude).toFixed(6)}, {parseFloat(updateModal.booking.slongitude).toFixed(6)}</p>
                  </div>
                </div>
              )}

              {/* Map */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-700">🗺️ Click on Map to Select Locations</label>
                <div className="h-[300px] rounded-lg overflow-hidden border border-gray-200">
                  <MapContainer center={updateModal.mapCenter} zoom={13} style={{ height: "100%", width: "100%" }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <ModalLocationPicker updateModal={updateModal} setUpdateModal={setUpdateModal} isInProgress={isInProgress(updateModal.booking.status)} />
                    {updateModal.newStartLat && updateModal.newStartLng && (
                      <Marker position={[parseFloat(updateModal.newStartLat), parseFloat(updateModal.newStartLng)]} icon={startIcon}>
                        <Popup>Pickup Location</Popup>
                      </Marker>
                    )}
                    {updateModal.newEndLat && updateModal.newEndLng && (
                      <Marker position={[parseFloat(updateModal.newEndLat), parseFloat(updateModal.newEndLng)]} icon={endIcon}>
                        <Popup>Destination</Popup>
                      </Marker>
                    )}
                    {updateModal.newStartLat && updateModal.newStartLng && updateModal.newEndLat && updateModal.newEndLng && (
                      <ModalRouteLayer
                        start={[parseFloat(updateModal.newStartLat), parseFloat(updateModal.newStartLng)]}
                        end={[parseFloat(updateModal.newEndLat), parseFloat(updateModal.newEndLng)]}
                        onRouteUpdate={(routeInfo) => setUpdateModal(prev => ({ ...prev, routeInfo }))}
                      />
                    )}
                  </MapContainer>
                </div>
                <p className="text-xs text-gray-500">
                  {!isInProgress(updateModal.booking.status) ? "Click: First = Pickup, Second = Destination" : "Click: Only destination can be updated"}
                </p>
              </div>

              {/* End Location */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-700">🎯 Destination Location</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search destination..."
                    value={updateModal.endSearch}
                    onChange={(e) => handleEndSearch(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
                  />
                  {updateModal.endSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg mt-1 max-h-48 overflow-y-auto shadow-lg z-50">
                      {updateModal.endSuggestions.map((feature, idx) => (
                        <button key={idx} onClick={() => selectEndLocation(feature)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 border-b last:border-b-0">
                          {feature.properties.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {updateModal.newEndLat && updateModal.newEndLng && (
                  <p className="text-xs text-gray-500 font-mono">{updateModal.newEndLat}, {updateModal.newEndLng}</p>
                )}
              </div>

              {/* Clear Button */}
              {(updateModal.newStartLat || updateModal.newEndLat) && (
                <div className="flex justify-center">
                  <button onClick={clearMapLocations} className="px-3 py-1.5 text-xs text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition">
                    Clear All Locations
                  </button>
                </div>
              )}

              {/* Distance Display */}
              {updateModal.routeInfo && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-700"><strong>Distance:</strong> {(updateModal.routeInfo.distance / 1000).toFixed(2)} km</p>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                <p className="text-xs text-blue-700">
                  {!isInProgress(updateModal.booking.status)
                    ? "You can update both pickup and destination locations using search or map click."
                    : "Pickup location is locked since the trip is in progress. You can only update the destination."}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-2">
              <button onClick={closeUpdateModal} className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
                Cancel
              </button>
              <button
                onClick={handleModalLocationUpdate}
                disabled={!updateModal.booking || !updateModal.newEndLat || !updateModal.newEndLng || (!isInProgress(updateModal.booking.status) && (!updateModal.newStartLat || !updateModal.newStartLng))}
                className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-lg transition ${
                  !updateModal.booking || !updateModal.newEndLat || !updateModal.newEndLng || (!isInProgress(updateModal.booking.status) && (!updateModal.newStartLat || !updateModal.newStartLng))
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-gray-900 text-white hover:bg-gray-800"
                }`}
              >
                Update Locations
              </button>
            </div>
          </div>
        </div>
      )}
    </EmployeeLayout>
  );
}